import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import LeadTenantForm from '../LeadTenantForm/LeadTenantForm';
import AddContactRole from '../AddContactRole/AddContactRole';
import CheckboxInput from '../../atoms/CheckboxInput/CheckboxInput';
import { ROLES } from '../../../appConstants';
import { UserContext } from '../../../providers/UserContext';
import { updateOpportunityGuarantorRequired } from '../../../api/salesforce';
import { WalesContext } from '../../../providers/WalesContext';
import householdFormDefinition from '../../../config/householdFormDefinition';
import { yourHouseholdCopy } from '../../../content/main.json';
import './HouseholdForm.scss';

const {
  leadTenantFields,
  additionalTenantFields,
  additionalOccupantFields,
  extensionFields,
  guarantorFields,
} = householdFormDefinition;

const HouseholdForm = ({ appHistory, initialWillUseProfessionalGuarantor }) => {
  const { isCompanyLet, opportunityId } = useContext(UserContext);
  const { isWales } = useContext(WalesContext);
  const [willUseProfessionalGuarantor, setWillUseProfessionalGuarantor] = useState(initialWillUseProfessionalGuarantor);
  const [updatingProfessionalGuarantor, setUpdatingProfessionalGuarantor] = useState(false);
  const yourHouseholdTerms = !isWales ? yourHouseholdCopy : yourHouseholdCopy.wales;

  const {
    leadTenantCopy,
    additionalTenantCopy,
    additionalOccupantCopy,
    guarantorCopy,
    professionalGuarantorCopy,
  } = yourHouseholdTerms;

  useEffect(() => {
    setWillUseProfessionalGuarantor(initialWillUseProfessionalGuarantor);
  }, [initialWillUseProfessionalGuarantor]);

  const handleProfessionalGuarantorChange = async () => {
    if (updatingProfessionalGuarantor) {
      return;
    }

    const nextValue = !willUseProfessionalGuarantor;
    setWillUseProfessionalGuarantor(nextValue);
    setUpdatingProfessionalGuarantor(true);

    try {
      await updateOpportunityGuarantorRequired(opportunityId, nextValue);
    } catch (error) {
      console.error('[HouseholdForm] Unable to update professional guarantor flag.', error);
      setWillUseProfessionalGuarantor(!nextValue);
    } finally {
      setUpdatingProfessionalGuarantor(false);
    }
  };

  return (
    <div className="householdForm">
      { isCompanyLet && (
      <p><em>{yourHouseholdTerms.companyLet}</em></p>
      )}
      <br />
      <LeadTenantForm
        copy={leadTenantCopy}
        fields={leadTenantFields}
      />
      <AddContactRole
        copy={additionalTenantCopy}
        fields={additionalTenantFields}
        sfRole={ROLES.TENANT}
        appHistory={appHistory}
        buttonTestId="addTenantButton"
      />
      <AddContactRole
        copy={additionalOccupantCopy}
        fields={additionalOccupantFields}
        extensionFields={extensionFields}
        sfRole={ROLES.OCCUPIER}
        buttonTestId="addPermittedOccupierButton"
      />

      <AddContactRole
        copy={guarantorCopy}
        fields={guarantorFields}
        sfRole={ROLES.GUARANTOR}
        buttonTestId="addGuarantorButton"
      />
      <div className="householdForm__professionalGuarantor" data-testid="professional-guarantor-section">
        <div className="householdForm__professionalGuarantorCopy">
          {professionalGuarantorCopy.information.map((paragraph) => (
            // eslint-disable-next-line react/no-danger
            <p key={paragraph} dangerouslySetInnerHTML={{ __html: paragraph }} />
          ))}
        </div>
        <CheckboxInput
          id="professionalGuarantorService"
          name="professionalGuarantorService"
          value="professionalGuarantorService"
          className="householdForm__professionalGuarantorCheckbox"
          label={professionalGuarantorCopy.checkboxLabel}
          iconPosition="right"
          checked={willUseProfessionalGuarantor}
          onChange={handleProfessionalGuarantorChange}
        />
      </div>
    </div>
  );
};

HouseholdForm.propTypes = {
  appHistory: PropTypes.instanceOf(Object),
  initialWillUseProfessionalGuarantor: PropTypes.bool,
};

HouseholdForm.defaultProps = {
  appHistory: {},
  initialWillUseProfessionalGuarantor: false,
};

export default HouseholdForm;
